import React, { useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, FormInstance, Modal, Button, ModalProps, SelectPicker } from 'rsuite';
import { BaseErrorModel, BaseResponseModel} from '@/models/core.model';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import { AdvanceShare, AdvanceShareAsset, AdvanceShareGenerateRequest } from '@/models/advance-share.model';
import { getActivityInfosLookup } from '@/services/activity-info.service';
import { generateAdvanceShare } from '@/services/advance-share.service';
import { AdvanceShareStyleVersion } from '@/models/advance-share-style-version.model';
import { AdvanceShareStyleAiAspectRatioEnum, AdvanceShareStyleAiAspectRatioList, AdvanceShareStyleAssetDefinition, AdvanceShareStyleAssetTypeEnum, AdvanceShareStyleVariableDefinition, AdvanceShareStyleVariableTypeEnum } from '@/models/advance-share-style-definition.model';
import GenerateResultModal from './advance-share-generate-result-modal';
import KeyValueList from './key-value-list-component';
import AssetList from './asset-list-component';
import { toastErrorMessage } from '@/utils/toasterMessage';

const marathonSchema = Schema.Model<AdvanceShareGenerateRequest>({
    activityInfoId: Schema.Types.NumberType().isRequired('This field is required.'),
    styleVersionId: Schema.Types.NumberType().isRequired('This field is required.'),
    userImageUrl: Schema.Types.StringType().isRequired('This field is required.'),
    aspectRatio: Schema.Types.NumberType(),
    gearUrl: Schema.Types.StringType(),
    gearId: Schema.Types.NumberType(),
    customVariables: Schema.Types.ObjectType(),
});


const emptyModel: AdvanceShareGenerateRequest = {
  activityInfoId: undefined,
  styleVersionId: undefined,
  userImageUrl: undefined,
  gearUrl: undefined,
  gearId: undefined,
  customVariables: undefined,
  aspectRatio: AdvanceShareStyleAiAspectRatioEnum.One_One
};

interface GenerateAdvanceShareActionModalProps extends ModalProps{
  styleVersion: AdvanceShareStyleVersion;
};

const GenerateAdvanceShareActionModal: React.FC<GenerateAdvanceShareActionModalProps> = ({styleVersion, open, onClose}) => {
  const [formValue, setFormValue] = useState<AdvanceShareGenerateRequest>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [customVariables, setCustomVariables] = useState<Record<string, string>>({});
  const [assets, setAssets] = useState<Record<string, AdvanceShareAsset>>({});
	
  const [resultModalShow, setResultModalShow] = useState(false);
  const [resultPromise, setResultPromise] = useState<Promise<BaseResponseModel<AdvanceShare>> | null>(null);

  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

	const handleSubmit = async () => {
			if(!styleVersion) return;

			if (formRef.current && !formRef.current.check()) {
					console.log("errors:", formError);
					return;
			}
			setErrorModel(undefined);
			setLoading(true);
			try {
				const requiredVariables = styleVersion.variables.filter(v => v.required && v.type == AdvanceShareStyleVariableTypeEnum.CustomVariable);
				if(requiredVariables.some(req => customVariables[req.key!] === "")){
					toastErrorMessage("Set value for all custom variables.");
					return;
				}
				
				const requiredAssets = styleVersion.assets.filter(v => v.required && (v.type == AdvanceShareStyleAssetTypeEnum.UserImage || v.type == AdvanceShareStyleAssetTypeEnum.GearImage));
				if(requiredAssets.some(req => !assets[req.key!].id && !assets[req.key!].url)){
					toastErrorMessage("Set value for all assets.");
					return;
				}

				const castedValue = {...formValue, customVariables: customVariables, assets: assets};
				const promise = generateAdvanceShare(castedValue);
				setResultPromise(promise);
				setResultModalShow(true);
				handleModalClose();

			} catch (err) {
				setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
			}finally{
				setLoading(false);
			}
	};

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  const handleModalClose = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
    setFormValue(emptyModel);
    setErrorModel(undefined);
    setLoading(false);
    if(onClose){
      onClose(event);
    }
  };

  const handleModalOpen = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
        if(!styleVersion) return;

        const hasUserImg = styleVersion.assets.some(asset => asset.type == AdvanceShareStyleAssetTypeEnum.UserImage);
        const hasGearImg = styleVersion.assets.some(asset => asset.type == AdvanceShareStyleAssetTypeEnum.GearImage);
        
        setFormValue(pre => ({...pre, 
            styleVersionId: styleVersion.id,
            userImageUrl: hasUserImg ? process.env.SAMPLE_USER_IMAGE_URL : '',
            gearUrl: hasGearImg ? process.env.SAMPLE_GEAR_IMAGE_URL : '',
        }));
        
        // Card backgrounds are sent as a key too, so they get an input here;
        // prefilled with the default so a preview works out of the box.
        const varRecord: Record<string, string> = styleVersion.variables
            .filter(v => v.type == AdvanceShareStyleVariableTypeEnum.CustomVariable ||
                v.type == AdvanceShareStyleVariableTypeEnum.RunDnaCardBackground)
            .reduce((acc, item: AdvanceShareStyleVariableDefinition) => {
                acc[item.key!] = item.type == AdvanceShareStyleVariableTypeEnum.RunDnaCardBackground ? 'holo' : '';
                return acc;
            }, {} as Record<string, string>);

        setCustomVariables(varRecord);

        const assetRecord: Record<string, AdvanceShareAsset> = styleVersion.assets
            .filter(v => 
							v.type == AdvanceShareStyleAssetTypeEnum.UserImage || 
							v.type == AdvanceShareStyleAssetTypeEnum.GearImage)
            .reduce((acc, item: AdvanceShareStyleAssetDefinition) => {
                acc[item.key!] = {
									type: item.type, 
									url: item.type == AdvanceShareStyleAssetTypeEnum.UserImage ? 
										process.env.SAMPLE_USER_IMAGE_URL : 
										process.env.SAMPLE_GEAR_IMAGE_URL, 
									id: undefined
								};
                return acc;
            }, {} as Record<string, AdvanceShareAsset>);

        setAssets(assetRecord);
  };

  return (
		<>
    <Modal open={open} onClose={handleModalClose} size="lg" backdrop={'static'} onOpen={handleModalOpen}>
        <Modal.Header>
            <Modal.Title>Generate Advance Share Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form name='generate-form' fluid 
                ref={formRef}
                onChange={handleFormChange}
                onCheck={setFormError}
                formValue={formValue}
                model={marathonSchema}
                disabled={loading}
            >
            <Grid w='100%' fluid>
                
                <Row>
                    <Col span={{xs:20}} >
                        <Form.Group controlId='activityInfoId'>
                            <Form.Label>Activity Info</Form.Label>
                            <Form.Control name="activityInfoId" accepter={FetchingSelect} block
                            fetchData={(text, page) => getActivityInfosLookup(text, page)} 
                            placeholder="Select a activity info"
                            /> 
                        </Form.Group>
                    </Col>
                    <Col span={{xs:4}} >
                        <Form.Group controlId='aspectRatio'>
                            <Form.Label>Aspect Ratio</Form.Label>
                            <Form.Control name="aspectRatio" accepter={SelectPicker} block
                                data={AdvanceShareStyleAiAspectRatioList} 
                                placeholder="Select an aspect ratio"
                            /> 
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col span={{xs:24}} >
                        <AssetList value={assets} onChange={setAssets}/>
                    </Col>
                </Row>
                <Row>
                    <Col span={{xs:24}} >
                        <KeyValueList value={customVariables} onChange={setCustomVariables}/>
                    </Col>
                </Row>
                   
            </Grid>
            </Form>
            {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
        </Modal.Body>
        <Modal.Footer>
            <Stack justifyContent='space-between' style={{paddingTop: 15}}>
                <Button onClick={handleModalClose} appearance="subtle" disabled={loading}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} appearance="primary" loading={loading}>
                    Generate
                </Button>
            </Stack>
        </Modal.Footer>       
    </Modal>
		{resultPromise && (
      <GenerateResultModal
        open={resultModalShow}
        onClose={() => setResultModalShow(false)}
        promise={resultPromise}
        extractImageUrl={(r: BaseResponseModel<AdvanceShare>) => r.result.generatedImageUrl}
      />
    )}
	</>
  );
};

export default GenerateAdvanceShareActionModal;

